import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { crm, fmtDateTime, fromNow } from '../../api/crm';
import { useSales } from '../../context/SalesContext';
import { Tag } from './leads/ui';

// Requests: a team leader (or a support user) writes whatever they need from the admin;
// Orion splits it into items; the admin approves or rejects each one (changes the CRM can
// make are applied on approval, the rest are suggestions of what to do by hand).

const STATUS_TONE = { pending: 'warning', approved: 'success', applied: 'success', done: 'success', rejected: 'error', failed: 'error', applying: 'info' };
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', applied: 'Applied', done: 'Done', rejected: 'Rejected', failed: 'Failed', applying: 'Applying' };

const StatusTag = ({ status }) => <Tag tone={STATUS_TONE[status] || 'neutral'} dot>{STATUS_LABEL[status] || status}</Tag>;

const useToast = () => {
  const [toast, setToast] = useState(null);
  const node = (
    <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
    </Snackbar>
  );
  return [node, (message, severity = 'success') => setToast({ message, severity })];
};

// ---------------------------------------------------------------- leader

const LeaderRequests = () => {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [hours, setHours] = useState(48);
  const [sending, setSending] = useState(false);
  const [toastNode, toast] = useToast();

  const load = useCallback(async () => {
    try {
      const d = await crm('leader-requests/mine');
      setRows(d.requests || []);
      setHours(d.window_hours || 48);
    } catch (e) {
      setRows((prev) => prev || []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a request is still being read, keep checking until it is ready.
  const reading = (rows || []).some((r) => r.status === 'interpreting');
  useEffect(() => {
    if (!reading) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [reading, load]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await crm('leader-requests', { method: 'POST', body: { text } });
      setText('');
      toast('Sent to the admin');
      await load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (id) => {
    try {
      await crm(`leader-requests/${id}/withdraw`, { method: 'POST' });
      await load();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 860 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Ask the admin</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Write anything you need done, several things at once if you like. Include the customer&apos;s number. The admin reviews
          requests every day at 5 PM.
        </Typography>
        <TextField
          multiline
          minRows={4}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 4000))}
          placeholder={'e.g. Reassign 98XXXXXXXX to Riya.\nAdd 1 month to 97XXXXXXXX, his payment was late.'}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
          <Typography variant="caption" color="text.secondary">{text.length} / 4000</Typography>
          <Button variant="contained" onClick={submit} disabled={sending || !text.trim()}>{sending ? 'Sending…' : 'Send request'}</Button>
        </Box>
      </Paper>

      <Typography variant="subtitle2" color="text.secondary">Your requests in the last {hours} hours</Typography>
      {rows === null && <CircularProgress size={22} />}
      {rows !== null && rows.length === 0 && (
        <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">Nothing sent in the last {hours} hours.</Typography></Paper>
      )}
      {(rows || []).map((r) => (
        <Paper key={r.id} sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={1}>
            <Typography variant="caption" color="text.secondary">{fmtDateTime(r.created_at)} · {fromNow(r.created_at)}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {r.status === 'interpreting' && <Tag tone="info" dot>Being read</Tag>}
              {r.status === 'withdrawn' && <Tag>Withdrawn</Tag>}
              {r.can_withdraw && <Button size="small" color="inherit" onClick={() => withdraw(r.id)}>Withdraw</Button>}
            </Box>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.text}</Typography>
          {r.status === 'ready' && (r.items || []).length > 0 && (
            <Stack spacing={0.75} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              {r.items.map((it) => (
                <Box key={it.seq} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {r.items.length > 1 && <Typography variant="caption" color="text.secondary">Part {it.seq}</Typography>}
                  <StatusTag status={it.status} />
                  {it.note && <Typography variant="body2" color="text.secondary">{it.note}</Typography>}
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      ))}
      {toastNode}
    </Stack>
  );
};

// ---------------------------------------------------------------- admin

// The change an "auto" item makes when approved, in one line.
const proposedChange = (it) => {
  const p = it.params || {};
  if (it.action === 'reassign') return `Reassign ${(p.numbers || []).join(', ')} to ${p.to}`;
  if (it.action === 'activate_plan') {
    const what = (p.plan_assets || []).join(', ') || p.plan_name || '';
    return `Activate ${p.type} ${what} for ${p.access_months} month(s) for ${p.number} (amount ₹${p.amount || 0})`;
  }
  if (it.action === 'add_days') return `Add ${p.buffer_days} day(s) to ${p.algo} for ${p.number}`;
  if (it.action === 'change_number') {
    return `Move the account from ${p.old_number} to ${p.new_number} (an account already on the new number is replaced)`;
  }
  if (it.action === 'move_plan') {
    return `Move the plan and its payments from ${p.from_number} to ${p.to_number} (both accounts stay)`;
  }
  if (it.action === 'change_plan') {
    return `Change payment ${p.rtransaction_id} of ${p.number} from ${p.from_plan} to ${p.to_plan} (same end date, no months added)`;
  }
  if (it.action === 'reassign_payment') return `Credit payment ${p.rtransaction_id} to ${p.to}`;
  if (it.action === 'approve_demat_kyc') {
    if (p.replace) {
      return `Change the client id of ${p.number} to ${p.client_id} and mark broker (demat) KYC approved (old account's session, password and auto-login are cleared) — confirm in Enrich first`;
    }
    return `Map client id ${p.client_id} to ${p.number} if needed and mark broker (demat) KYC approved — confirm in Enrich first`;
  }
  if (it.action === 'plan_override') {
    const rupees = (v) => (Number(v) === -1 ? 'unlimited' : `₹${Number(v).toLocaleString('en-IN')}`);
    return `Exception for ${p.number} on ${p.plan_id}: multiplier x${p.multiplier}, investing capital ${rupees(p.investing_capital_allowed)}, trading capital ${rupees(p.trading_capital_allowed)}`;
  }
  return '';
};

const isOpen = (it) => it.status === 'pending' || it.status === 'failed';

const AdminRequests = () => {
  const [scope, setScope] = useState('pending');
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toastNode, toast] = useToast();

  const load = useCallback(async () => {
    try {
      const d = await crm('leader-requests/admin', { query: { status: scope } });
      setRows(d.requests || []);
      setError('');
    } catch (e) {
      setError(e.message);
      setRows((prev) => prev || []);
    }
  }, [scope]);

  useEffect(() => { setRows(null); setSelected({}); load(); }, [load]);

  // While Orion is reading a request, keep checking until its items arrive.
  const reading = (rows || []).some((r) => r.status === 'interpreting');
  useEffect(() => {
    if (!reading) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [reading, load]);

  const openItems = useMemo(
    () => (rows || []).filter((r) => r.status === 'ready').flatMap((r) => (r.items || []).filter(isOpen)),
    [rows]
  );
  const chosen = openItems.filter((it) => selected[it.id]);
  const allChosen = openItems.length > 0 && chosen.length === openItems.length;

  const decide = async (items, approve) => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const d = await crm('leader-requests/decide', {
        method: 'POST',
        body: { decisions: items.map((it) => ({ item_id: it.id, approve })), note },
      });
      const failed = (d.results || []).filter((r) => r.status === 'failed');
      if (failed.length > 0) toast(`${failed.length} could not be applied: ${failed[0].result}`, 'error');
      else toast(approve ? `${items.length} approved` : `${items.length} rejected`);
      setSelected({});
      setNote('');
      await load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Have Orion read a request again: its waiting items are replaced, decided ones stay.
  const reanalyze = async (r) => {
    setBusy(true);
    try {
      await crm(`leader-requests/${r.id}/reanalyze`, { method: 'POST' });
      toast('Orion is reading it again');
      setSelected({});
      await load();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Team leader requests</Typography>
          <Typography variant="body2" color="text.secondary">
            Approving an item marked &quot;Applies on approval&quot; makes that change. Items marked &quot;By hand&quot; are only marked done.
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="pending">Waiting</ToggleButton>
          <ToggleButton value="all">Last 30 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {openItems.length > 0 && (
        <Paper sx={{ p: 1.5, position: 'sticky', top: 8, zIndex: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Box display="flex" alignItems="center">
              <Checkbox
                size="small"
                checked={allChosen}
                indeterminate={chosen.length > 0 && !allChosen}
                onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(openItems.map((it) => [it.id, true])) : {})}
                inputProps={{ 'aria-label': 'Select all waiting items' }}
              />
              <Typography variant="body2">{chosen.length} of {openItems.length} selected</Typography>
            </Box>
            <TextField size="small" placeholder="Note to the leader (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
            <Button variant="contained" disabled={busy || chosen.length === 0} onClick={() => decide(chosen, true)}>Approve selected</Button>
            <Button color="error" disabled={busy || chosen.length === 0} onClick={() => decide(chosen, false)}>Reject selected</Button>
          </Box>
        </Paper>
      )}

      {rows === null && <CircularProgress size={22} />}
      {rows !== null && rows.length === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">{scope === 'pending' ? 'Nothing is waiting for you.' : 'No requests in the last 30 days.'}</Typography>
        </Paper>
      )}

      {(rows || []).map((r) => (
        <Paper key={r.id} sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.75}>
            <Typography variant="subtitle2" fontWeight={700}>{r.leader || 'Admin'}</Typography>
            {r.raised_by_role === 'support' && <Tag tone="purple">Support</Tag>}
            <Typography variant="caption" color="text.secondary">{fmtDateTime(r.created_at)} · {fromNow(r.created_at)}</Typography>
            {r.status === 'interpreting' && <Tag tone="info" dot>Orion is reading it</Tag>}
            {r.status === 'withdrawn' && <Tag>Withdrawn</Tag>}
            {r.status === 'ready' && (
              <Button size="small" disabled={busy} onClick={() => reanalyze(r)} sx={{ ml: 'auto' }} title="Orion reads this request again. Items still waiting are replaced; decided items stay.">
                Re-analyse
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{r.text}</Typography>
          {r.interpret_error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>Not interpreted: {r.interpret_error}</Typography>
          )}

          {(r.items || []).map((it) => {
            const open = r.status === 'ready' && isOpen(it);
            return (
              <Box key={it.id} display="flex" gap={1} sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <Checkbox
                  size="small"
                  sx={{ alignSelf: 'flex-start', mt: -0.5, visibility: open ? 'visible' : 'hidden' }}
                  checked={Boolean(selected[it.id])}
                  onChange={(e) => setSelected((prev) => ({ ...prev, [it.id]: e.target.checked }))}
                  inputProps={{ 'aria-label': `Select: ${it.summary}` }}
                />
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Tag tone={it.kind === 'auto' ? 'primary' : 'neutral'}>{it.kind === 'auto' ? 'Applies on approval' : 'By hand'}</Tag>
                    <Typography variant="body2" fontWeight={600}>{it.summary}</Typography>
                    {it.status !== 'pending' && <StatusTag status={it.status} />}
                  </Box>
                  {it.context && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{it.context}</Typography>}
                  {it.kind === 'auto' && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Change:</b> {proposedChange(it)}</Typography>}
                  {it.kind === 'auto' && it.caution && (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}><b>Check before approving:</b> {it.caution}</Typography>
                  )}
                  {it.kind !== 'auto' && it.manual_steps && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}><b>Suggested:</b> {it.manual_steps}</Typography>
                  )}
                  {it.result && <Typography variant="body2" color={it.status === 'failed' ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>{it.result}</Typography>}
                  {it.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Your note: {it.note}</Typography>}
                </Box>
                {open && (
                  <Box display="flex" gap={0.5} alignSelf="flex-start" flexShrink={0}>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => decide([it], true)}>{it.status === 'failed' ? 'Retry' : 'Approve'}</Button>
                    <Button size="small" color="error" disabled={busy} onClick={() => decide([it], false)}>Reject</Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      ))}
      {toastNode}
    </Stack>
  );
};

const Requests = () => {
  const sales = useSales();
  return sales.isAdmin ? <AdminRequests /> : <LeaderRequests />;
};

export default Requests;
